/**
 * Email Filters Component
 * Advanced filtering dialog for emails
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Box,
  Typography,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { EmailLabel } from '../../services/emailService';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 3,
    maxWidth: '600px',
    width: '100%'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
  '& .MuiTypography-root': {
    fontWeight: 600
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  '&:first-of-type': {
    paddingTop: theme.spacing(3)
  }
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50]
}));

const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTypography-h6': {
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '1rem'
  }
}));

interface EmailFiltersProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: any) => void;
  currentFilters: any;
  labels: EmailLabel[];
}

const EmailFilters: React.FC<EmailFiltersProps> = ({
  open,
  onClose,
  onApply,
  currentFilters,
  labels
}) => {
  // Filter state
  const [status, setStatus] = useState(currentFilters.status || '');
  const [direction, setDirection] = useState(currentFilters.direction || '');
  const [selectedLabels, setSelectedLabels] = useState<string[]>(currentFilters.labels || []);
  const [linkedEntityType, setLinkedEntityType] = useState(currentFilters.linkedEntityType || '');
  const [linkedEntityId, setLinkedEntityId] = useState(currentFilters.linkedEntityId || '');
  const [dateFrom, setDateFrom] = useState<string>(currentFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState<string>(currentFilters.dateTo || '');
  const [hasAttachments, setHasAttachments] = useState(currentFilters.hasAttachments || false);
  const [isStarred, setIsStarred] = useState(currentFilters.isStarred || false);
  const [senderEmail, setSenderEmail] = useState(currentFilters.senderEmail || '');

  // Reset filters when dialog opens
  useEffect(() => {
    if (open) {
      setStatus(currentFilters.status || '');
      setDirection(currentFilters.direction || '');
      setSelectedLabels(currentFilters.labels || []);
      setLinkedEntityType(currentFilters.linkedEntityType || '');
      setLinkedEntityId(currentFilters.linkedEntityId || '');
      setDateFrom(currentFilters.dateFrom || '');
      setDateTo(currentFilters.dateTo || '');
      setHasAttachments(currentFilters.hasAttachments || false);
      setIsStarred(currentFilters.isStarred || false);
      setSenderEmail(currentFilters.senderEmail || '');
    }
  }, [open, currentFilters]);

  const handleApply = () => {
    const filters: any = {};

    if (status) filters.status = status;
    if (direction) filters.direction = direction;
    if (selectedLabels.length > 0) filters.labels = selectedLabels;
    if (linkedEntityType) filters.linkedEntityType = linkedEntityType;
    if (linkedEntityId) filters.linkedEntityId = linkedEntityId;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    if (hasAttachments) filters.hasAttachments = true;
    if (isStarred) filters.isStarred = true;
    if (senderEmail) filters.senderEmail = senderEmail;

    onApply(filters);
  };

  const handleClear = () => {
    setStatus('');
    setDirection('');
    setSelectedLabels([]);
    setLinkedEntityType('');
    setLinkedEntityId('');
    setDateFrom('');
    setDateTo('');
    setHasAttachments(false);
    setIsStarred(false);
    setSenderEmail('');
    onApply({});
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (status) count++;
    if (direction) count++;
    if (selectedLabels.length > 0) count++;
    if (linkedEntityType) count++;
    if (linkedEntityId) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    if (hasAttachments) count++;
    if (isStarred) count++;
    if (senderEmail) count++;
    return count;
  };

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <StyledDialogTitle>
          <Typography variant="h6">
            Email Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()} active)`}
          </Typography>
        </StyledDialogTitle>

        <StyledDialogContent>
          {/* Status and Direction */}
          <FilterSection>
            <Typography variant="h6">Status & Direction</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="inbound">Inbound</MenuItem>
                  <MenuItem value="outbound">Outbound</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </FilterSection>

          {/* Special Filters */}
          <FilterSection>
            <Typography variant="h6">Special Filters</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasAttachments}
                    onChange={(e) => setHasAttachments(e.target.checked)}
                  />
                }
                label="Has Attachments"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isStarred}
                    onChange={(e) => setIsStarred(e.target.checked)}
                  />
                }
                label="Starred Only"
              />
            </Box>
          </FilterSection>

          {/* Labels */}
          {labels.length > 0 && (
            <FilterSection>
              <Typography variant="h6">Labels</Typography>
              <Autocomplete
                multiple
                options={labels.map(label => label.name)}
                value={selectedLabels}
                onChange={(_, newValue) => setSelectedLabels(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select labels"
                    size="small"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const label = labels.find(l => l.name === option);
                    return (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={label ? {
                          backgroundColor: label.color,
                          color: 'white'
                        } : {}}
                      />
                    );
                  })
                }
              />
            </FilterSection>
          )}

          {/* Entity Linking */}
          <FilterSection>
            <Typography variant="h6">Entity Linking</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={linkedEntityType}
                  onChange={(e) => setLinkedEntityType(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="order">Order</MenuItem>
                  <MenuItem value="case">Case</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="supplier">Supplier</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Entity ID"
                value={linkedEntityId}
                onChange={(e) => setLinkedEntityId(e.target.value)}
                size="small"
                placeholder="Enter ID"
                disabled={!linkedEntityType}
              />
            </Box>
          </FilterSection>

          {/* Date Range */}
          <FilterSection>
            <Typography variant="h6">Date Range</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="From Date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="To Date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </FilterSection>

          {/* Sender Email */}
          <FilterSection>
            <Typography variant="h6">Sender</Typography>
            <TextField
              fullWidth
              label="Sender Email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              size="small"
              placeholder="Enter sender email address"
            />
          </FilterSection>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button onClick={handleClear} color="secondary">
            Clear All
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} variant="contained">
            Apply Filters
          </Button>
        </StyledDialogActions>
      </StyledDialog>
  );
};

export default EmailFilters;
